<template>
  <v-dialog
    :value="isConfigurarAssociacaoFilaFormularioDialogOpen"
    max-width="800px"
    persistent
  >
    <v-card class="pa-sm-8 pa-2">
      <btn-close-dialog :handle-click="closeDialog" />

      <v-card-title class="justify-center text-h5 pt-0">
        {{ dialogTitle }}
      </v-card-title>

      <v-card-text class="mt-5">
        <v-form
          ref="formRef"
          class="multi-col-validation"
          @submit.prevent="handleSubmit"
        >
          <v-row>
            <v-col
              v-if="isNovaAssociacao"
              cols="12"
            >
              <v-select
                v-model="filaFormularioData.formularioId"
                label="Formulários"
                item-text="formularioNome"
                item-value="formularioId"
                dense
                outlined
                validate-on-blur
                hide-details
                placeholder="Formulários"
                no-data-text="Nenhum formulário disponível"
                :items="formulariosNaoAssociados"
                :rules="[validators.select]"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-card
                outlined
                elevation="4"
              >
                <v-card-title class="pl-2 pb-2 pt-3">
                  Visibilidade:
                </v-card-title>

                <v-divider></v-divider>

                <v-row class="d-flex px-2">
                  <v-col
                    cols="auto"
                    class="d-flex flex-wrap"
                  >
                    <v-checkbox
                      v-model="filaFormularioData.visivelSolicitante"
                      label="Solicitante no autoatendimento"
                      class="mr-4"
                      @change="handleVisibilidadeSolicitanteChange"
                    />

                    <v-checkbox
                      v-model="filaFormularioData.visivelAgente"
                      label="Agente no atendimento"
                      @change="handleVisibilidadeAgenteChange"
                    />
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-card
                outlined
                elevation="4"
              >
                <v-card-title class="pl-2 pb-2 pt-3">
                  Edição:
                </v-card-title>

                <v-divider></v-divider>

                <v-row class="d-flex px-2">
                  <v-col
                    cols="auto"
                    class="d-flex flex-wrap"
                  >
                    <v-checkbox
                      v-model="filaFormularioData.editavelSolicitante"
                      :disabled="isVisibilidadeSolicitanteDesabilitada"
                      label="Solicitante no autoatendimento"
                      class="mr-4"
                    />

                    <v-checkbox
                      v-model="filaFormularioData.editavelAgente"
                      :disabled="isVisibilidadeAgenteDesabilitada"
                      label="Agente no atendimento"
                    />
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>

          <v-row v-if="!isSecaoDefault">
            <v-col cols="12">
              <v-card
                outlined
                elevation="4"
              >
                <v-card-title class="pl-2 pb-2 pt-3">
                  Impressão:
                </v-card-title>

                <v-divider></v-divider>

                <v-row class="d-flex px-2">
                  <v-col
                    cols="auto"
                    class="d-flex flex-wrap"
                  >
                    <v-checkbox
                      v-model="filaFormularioData.visivelSolicitanteImpressao"
                      :disabled="isVisibilidadeSolicitanteDesabilitada"
                      label="Solicitante no autoatendimento"
                      class="mr-4"
                    />

                    <v-checkbox
                      v-model="filaFormularioData.visivelAgenteImpressao"
                      :disabled="isVisibilidadeAgenteDesabilitada"
                      label="Agente no atendimento"
                    />
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>

          <v-row v-if="!isSecaoDefault">
            <v-col cols="12">
              <v-select
                v-model="filaFormularioData.ordem"
                label="Ordem"
                dense
                outlined
                validate-on-blur
                hide-details
                :items="ordensPermitidas"
                :rules="[validators.select]"
              />
            </v-col>
          </v-row>

          <v-row class="mt-5">
            <v-col
              cols="12"
              class="d-flex justify-center"
            >
              <v-btn
                color="secondary"
                outlined
                type="reset"
                class="mr-4"
                @click="resetFormData"
              >
                Cancelar
              </v-btn>

              <v-btn
                color="primary"
                type="submit"
              >
                <v-progress-circular
                  v-if="loading"
                  class="mx-6"
                  indeterminate
                  color="secundary"
                />
                <span v-else>{{ submitBtnTitle }}</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from 'vue'
import { ref, watchEffect, computed, watch } from '@vue/composition-api'
import { required, select } from '@core/utils/validation'
import router from '@/router'
import BtnCloseDialog from '@/components/btn-close-dialog/BtnCloseDialog.vue'
import FilaService from '@/services/modules/FilaService'
import useDialog from '@/composables/useDialog'
import FilaFormularioModel from '@/models/entities/fila/FilaFormularioModel'

export default {
  components: {
    BtnCloseDialog,
  },

  emits: ['update:is-configurar-associacao-fila-formulario-dialog-open'],

  props: {
    isConfigurarAssociacaoFilaFormularioDialogOpen: {
      type: Boolean,
      required: true,
    },
    associacaoData: {
      type: Object,
      default: null,
    },
    ordensPermitidas: {
      type: Array,
      default: null,
    },
  },

  setup(props, { emit }) {
    const filaFormularioData = ref(new FilaFormularioModel())
    function resetFilaFormularioData() {
      filaFormularioData.value = JSON.parse(JSON.stringify(props.associacaoData || new FilaFormularioModel()))
    }
    watch(() => props.associacaoData, resetFilaFormularioData, { immediate: true })

    const isVisibilidadeValid = computed(() => (!!filaFormularioData.value.visivelSolicitante || !!filaFormularioData.value.visivelAgente))
    const isVisibilidadeSolicitanteDesabilitada = computed(() => !filaFormularioData.value.visivelSolicitante)
    const isVisibilidadeAgenteDesabilitada = computed(() => !filaFormularioData.value.visivelAgente)

    const formulariosNaoAssociados = ref([])
    async function getFormulariosNaoAssociados() {
      formulariosNaoAssociados.value = await FilaService.getFormulariosNaoAssociados(router.currentRoute.params.id)
    }

    const isNovaAssociacao = computed(() => !props.associacaoData)
    const isSecaoDefault = computed(() => filaFormularioData.value.formularioId === null)

    const dialogTitle = computed(() => (isNovaAssociacao.value ? 'Associar Fila e Formulário' : 'Atualizar associação'))
    const submitBtnTitle = computed(() => (isNovaAssociacao.value ? 'Associar' : 'Atualizar'))

    function handleVisibilidadeSolicitanteChange(v) {
      filaFormularioData.value.editavelSolicitante = v
    }
    function handleVisibilidadeAgenteChange(v) {
      filaFormularioData.value.editavelAgente = v
    }

    if (isNovaAssociacao.value) {
      watchEffect(() => { if (props.isConfigurarAssociacaoFilaFormularioDialogOpen) { getFormulariosNaoAssociados() } })
    }

    const { formRef, loading, resetForm, closeDialog } = useDialog(emit, 'is-configurar-associacao-fila-formulario-dialog-open')

    function resetFormData() {
      if (isNovaAssociacao.value) resetForm()
      resetFilaFormularioData()
      closeDialog()
    }

    async function criarAssociacao() {
      try {
        loading.value = true
        await FilaService.associarFilaEFormulario(router.currentRoute.params.id, filaFormularioData.value)
        emit('refetch-data')
        resetFormData()
      } finally {
        loading.value = false
      }
    }

    async function atualizarAssociacao() {
      try {
        loading.value = true
        await FilaService.atualizarAssociacaoEntreFilaEFormulario(router.currentRoute.params.id, filaFormularioData.value)
        emit('refetch-data')
        closeDialog()
      } finally {
        loading.value = false
      }
    }

    async function handleSubmit() {
      if (loading.value) return

      if (!formRef.value.validate()) {
        Vue.$toast.warning('Preencha corretamente os campos antes de encaminhar')
        return
      }
      if (!isVisibilidadeValid.value) {
        Vue.$toast.warning('Ao menos um dos checkboxes de visibilidade deve ser marcado')
        return
      }
      if (isNovaAssociacao.value) {
        await criarAssociacao()
      } else {
        await atualizarAssociacao()
      }
    }

    return {
      filaFormularioData,
      isVisibilidadeSolicitanteDesabilitada,
      isVisibilidadeAgenteDesabilitada,
      formulariosNaoAssociados,
      isNovaAssociacao,
      isSecaoDefault,
      dialogTitle,
      submitBtnTitle,
      handleVisibilidadeSolicitanteChange,
      handleVisibilidadeAgenteChange,
      formRef,
      loading,
      closeDialog,
      resetFormData,
      handleSubmit,
      validators: { required, select },
    }
  },
}
</script>
